import classNames from 'classnames'
import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import Slider from 'react-slick'
import LinkLocale from '../../../../modules/i18n/LinkLocale'
import { LOCALE_KO } from '../../../../modules/i18n/config'
import { s_home } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'

import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import { CMSDesktopSection } from '../../../new-portal/pricing/CMSSection'
import { IMG_BUSINESS_CHECK_ICON, IMG_BUSINESS_SYSTEM_IMG_DESKTOP } from '../../constants/business.constants'
import {
  IMG_HOME_DESKTOP_COMPANY_ROW_ONE,
  IMG_HOME_DESKTOP_COMPANY_ROW_THREE,
  IMG_HOME_DESKTOP_COMPANY_ROW_TWO,
  IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_LEFT,
  IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_RIGHT,
  IMG_HOME_SECTION_ICON_REVIEW_V2,
} from '../../constants/home.constants'
import { BUSINESS_REVIEW_LIST } from '../../data/business.data'
import { IInquiryFormData } from '../../interface/business.interface'

const BusinessBody: React.FC = () => {
  const { t, locale } = useTranslation()
  const appRouter = useAppRouter()

  const {
    reset,
    formState: { isSubmitting, errors },
  } = useForm<IInquiryFormData>({ mode: 'onTouched' })

  const sliderRef = useRef<Slider>()
  const inquirySectionRef = useRef<HTMLDivElement>(null)

  const goToReviewPage = () => {
    appRouter.push(urls.student.landing.blog.tags('링글 후기'))
  }

  return (
    <div className="flex flex-col items-center justify-start">
      {/* Section 1 */}
      <div className="max-w-[1080px] px-20 pt-[68px] pb-[96px]">
        <div className="space-y-20">
          <img src={IMG_HOME_DESKTOP_COMPANY_ROW_ONE} alt="company-row-one" />
          <img src={IMG_HOME_DESKTOP_COMPANY_ROW_TWO} alt="company-row-one" />
          <img src={IMG_HOME_DESKTOP_COMPANY_ROW_THREE} alt="company-row-one" />
        </div>

        <div className="mt-[54px] text-center">
          <h1
            className={classNames(
              'whitespace-pre-line text-purple-500',
              locale === LOCALE_KO ? 'Hero2-48Bd' : 'Hero3-36Bd'
            )}
          >
            {t(s_home.business.title.desktop)}
          </h1>
          <p
            className={classNames(
              'mt-12 whitespace-pre-line text-[#585D71]',
              locale === LOCALE_KO ? 'Title1-20Md' : 'Title2-18Md'
            )}
          >
            {t(s_home.business.subtitle.desktop)}
          </p>

          <button
            onClick={() => inquirySectionRef?.current?.scrollIntoView()}
            className={classNames(
              'mt-32 min-w-[222px] rounded-[7px] bg-[#4130A4] px-12 py-16 text-white',
              locale === LOCALE_KO ? 'Body1-16Bd' : 'Body2-14Bd'
            )}
          >
            {t(s_home.business.cta.desktop)}
          </button>
        </div>
      </div>

      {/* Section 2 */}
      <div className="flex w-full items-center justify-center bg-[#F8F8FB]">
        <div className="w-full max-w-[1238px] px-20 pt-[174px] pb-[140px]">
          {/* Label Section */}
          <div className="flex items-center justify-center">
            <img src={IMG_HOME_SECTION_ICON_REVIEW_V2} alt="b2b-review-icon" className="h-[32px] w-[32px]" />
            <p
              className={classNames('ml-4 text-[#585D71]', locale === LOCALE_KO ? 'Headline2-24Bd' : 'Headline3-22Bd')}
            >
              {t(s_home.business_review.subtitle.desktop)}
            </p>
          </div>

          <h2
            className={classNames(
              'mt-16 whitespace-pre-line text-center text-black',
              locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
            )}
          >
            {t(s_home.business_review.title.desktop)}
          </h2>

          <div className="flex justify-center pt-[32px]">
            <DSButton
              type={BUTTON_TYPE.OUTLINE}
              size={BUTTON_SIZE.MD}
              className="max-w-[222px] h-[56px]"
              customBorderColor={COLORS.PURPLE}
              onClick={goToReviewPage}
            >
              <DSButton.Label customTxtColor={COLORS.PURPLE}>{t(['후기 더 알아보기', 'more reviews'])}</DSButton.Label>
            </DSButton>
          </div>

          <div className="mt-[32px]">
            <Slider
              ref={sliderRef}
              className="slider"
              slidesToShow={3}
              slidesToScroll={3}
              prevArrow={<></>}
              nextArrow={<></>}
            >
              {BUSINESS_REVIEW_LIST(locale, false).map((item) => (
                <div key={item.id} className="w-full py-12">
                  <div className="mx-[12px] rounded-xl bg-white py-[44px] px-[32px] shadow-[0px_2px_8px_rgba(0,0,0,0.15)]">
                    <img src={item.logoUrl} alt="logo-url" className="h-[52px]" />
                    <p className="Title1-20Md mt-[24px] break-words break-keep">{item.reviewText}</p>
                  </div>
                </div>
              ))}
            </Slider>

            <div className="mt-[52px] flex w-full items-center justify-center space-x-[24px]">
              <button onClick={() => sliderRef?.current?.slickPrev()}>
                <img src={IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_LEFT} alt="chevron-left" />
              </button>
              <button onClick={() => sliderRef?.current?.slickNext()}>
                <img src={IMG_HOME_DESKTOP_ICON_CHEVRON_CIRCLE_RIGHT} alt="chevron-right" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3 */}

      {/* Section 4 */}
      <div className="w-full max-w-[1080px] px-20 pt-[175px] pb-[145px]">
        {/* Label Section */}
        <div className="flex items-center justify-center">
          <img src={IMG_BUSINESS_CHECK_ICON} alt="b2b-service-icon" className="h-[32px] w-[32px]" />
          <p className={classNames('ml-4 text-[#585D71]', locale === LOCALE_KO ? 'Headline2-24Bd' : 'Headline3-22Bd')}>
            {t(s_home.business_manage.subtitle.desktop)}
          </p>
        </div>

        <h2
          className={classNames(
            'mt-16 whitespace-pre-line text-center text-black',
            locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
          )}
        >
          {t(s_home.business_manage.title.desktop)}
        </h2>

        <div className="mt-[71px] flex flex-col items-start justify-between rounded-xl bg-[#31247B] pt-[68px] pl-[72px] shadow-[0px_2px_8px_rgba(0,0,0,0.15)]">
          <div className="w-[415px]">
            <p
              className={classNames(
                'whitespace-pre-line text-white',
                locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
              )}
            >
              {t(s_home.business_manage.system_title.desktop)}
            </p>
            <p
              className={classNames(
                'mt-[20px] whitespace-pre-line text-[#F8F8FB]',
                locale === LOCALE_KO ? 'Title1-20Md' : 'Title2-18Md'
              )}
            >
              {t(s_home.business_manage.system_bodytxt_1.desktop)}
            </p>
            <p
              className={classNames(
                'mt-[12px] whitespace-pre-line text-[#F8F8FB]',
                locale === LOCALE_KO ? 'Title1-20Md' : 'Title2-18Md'
              )}
            >
              {t(s_home.business_manage.system_bodytxt_2.desktop)}
            </p>
          </div>

          <img
            src={IMG_BUSINESS_SYSTEM_IMG_DESKTOP}
            alt="system-img-desktop"
            className="mt-[56px] rounded-br-xl pl-[115px]"
          />
        </div>
      </div>

      {/* CMS Section */}
      <CMSDesktopSection slug={'b2b'} />

      {locale === LOCALE_KO && (
        <div className="flex w-full items-center justify-center bg-[#201852]">
          <div className="w-full max-w-[1080px] px-20 py-[64px] text-center">
            <div className="Hero3-36Bd mt-16 whitespace-pre-line text-center text-white">
              {t(s_home.business_export.subtitle.desktop)}
            </div>
            <div className="mt-[32px] w-full">
              <LinkLocale href={urls.student.landing.voucher}>
                <button
                  className={classNames(
                    'Body1-16Bd mt-32 min-w-[222px] cursor-pointer rounded-[7px] bg-[#4130A4] px-12 py-16 text-white'
                  )}
                >
                  {t(s_home.business_export.cta.desktop)}
                </button>
              </LinkLocale>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BusinessBody
